import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { UserService, UserDetails } from '@app/user/user.service';

@Component({
    selector: 'app-user-info-sidebar',
    templateUrl: './user-info-sidebar.component.html',
    styleUrls: ['./user-info-sidebar.component.scss'],
    standalone: false
})

export class UserInfoSidebarComponent implements OnInit, OnChanges {
    @Input() userId?: number;
    
    details: UserDetails | null = null;
    isLoading = false;
    error: string | null = null;
    
    // Note modal state
    showNoteModal: boolean = false;
    editedNote: string = '';
    isClosingNoteModal: boolean = false;
    showNoteError: boolean = false;

    constructor(private userService: UserService) {}
    
    ngOnInit(): void {
        if (this.userId) {
            this.loadUserDetails();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['userId'] && this.userId) {
            this.loadUserDetails();
        } else if (changes['userId'] && !this.userId) {
            // Clear details when userId is removed
            this.details = null;
        }
    }

    async loadUserDetails() {
        if (!this.userId) {
            return;
        }

        this.isLoading = true;
        this.error = null;

        try {
            const { data, error } = await this.userService.getUserDetails(this.userId);
            
            if (error) {
                this.error = 'Failed to load user details';
                this.details = null;
            } else {
                this.details = data;
            }
        } catch (err) {
            this.error = 'An error occurred while loading user details';
            this.details = null;
        } finally {
            this.isLoading = false;
        }
    }

    getInitials(): string {
        if (!this.details) return '?';
        if (!this.details.first_name && !this.details.last_name) return '?';
        
        const parts = (this.details.first_name + ' ' + this.details.last_name).trim().split(/\s+/);
        return parts.length >= 2
            ? (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
            : parts[0].charAt(0).toUpperCase();
    }

    getChangeClass(change: number | null | undefined): string {
        if (change == null || isNaN(change as number)) return 'text-muted';
        if (change === 0) return 'text-muted';
        return change > 0 ? 'text-success' : 'text-danger';
    }

    getChangeIcon(change: number | null | undefined): string {
        if (change == null || isNaN(change as number) || change === 0) return 'ti ti-minus';
        return change > 0 ? 'ti ti-arrow-up' : 'ti ti-arrow-down';
    }

    formatChange(change: number | null | undefined, unit: string): string {
        if (change == null || isNaN(change as number)) return '—';
        if (change === 0) return '0 ' + unit;
        const sign = change > 0 ? '+' : '';
        return `${sign}${change} ${unit}`;
    }

    openUserDetails(tab?: string, subTab?: string): void {
        if (!this.userId) return;

        const queryParts: string[] = [];
        if (tab) {
            queryParts.push(`tab=${encodeURIComponent(tab)}`);
        }
        if (subTab) {
            queryParts.push(`subTab=${encodeURIComponent(subTab)}`);
        }

        const query = queryParts.length ? `?${queryParts.join('&')}` : '';
        const url = `/user/user/details-mantis/${this.userId}${query}`;
        window.open(url, '_blank');
    }
}

